"use client";

import React, { useMemo, useRef } from "react";
import { Session, User } from "@supabase/supabase-js";
import { AuthProvider } from "@/contexts/AuthContext";
import { SupabaseListener } from "@/components/SupabaseListener";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import ModalContextProvider from "@/contexts/ModalContextProvider";
import ConfettiContextProvider from "@/contexts/ConfettiContextProvider";
import { DEMO_USER_EMAIL } from "@/constants";

// User context
export const UserContext = React.createContext<User | null>(null);

/**
 * This is a wrapper for the app that provides the supabase client, the router event wrapper
 * the react-query client, supabase listener, and the navigation progress bar.
 *
 * The listener is used to listen for changes to the user's session and update the UI accordingly.
 * Migrated from @supabase/auth-helpers-react to @supabase/ssr
 */
export default function AppProviders({
  children,
  initialSession,
  user
}: {
  children: React.ReactNode;
  initialSession: Session | null;
  user?: User;
}) {
  // REMOVED: usePathname() was causing ENTIRE tree to unmount/remount on navigation!
  // The pathname check for setup pages is now handled by individual components that need it.

  // Stable QueryClient - created once per component lifecycle
  const queryClientRef = useRef<QueryClient | null>(null);
  if (!queryClientRef.current) {
    queryClientRef.current = new QueryClient({
      defaultOptions: {
        queries: {
          // Optimize default query options for better UX
          staleTime: 60 * 1000,
          // 1 minute - reduces refetches
          refetchOnWindowFocus: false // Reduces unnecessary refetches
        }
      }
    });
  }

  // Memoize user value to prevent child re-renders when user reference changes
  // Only re-render when user ID changes (the meaningful change)
  const userValue = useMemo(() => user ?? null, [user?.id]);
  const isDemoUser = initialSession && initialSession.user.email === DEMO_USER_EMAIL;
  return <QueryClientProvider client={queryClientRef.current!}>
            <AuthProvider initialSession={initialSession} initialUser={user}>
                <SupabaseListener accessToken={initialSession?.access_token} />
                <UserContext.Provider value={userValue}>
                    <TooltipProvider delayDuration={200}>
                        {/* 
                          Always render ModalContextProvider - removed pathname dependency
                          that was causing entire tree to unmount/remount on navigation.
                          Demo user check moved to modal components themselves.
                         */}
                        {isDemoUser ? <ConfettiContextProvider>
                                {children}
                                <Toaster />
                            </ConfettiContextProvider> : <ModalContextProvider>
                                <ConfettiContextProvider>
                                    {children}
                                    <Toaster />
                                </ConfettiContextProvider>
                            </ModalContextProvider>}
                    </TooltipProvider>
                </UserContext.Provider>
            </AuthProvider>
        </QueryClientProvider>;
}